import { useState, useEffect } from "react";
import { CircleArrowLeft } from "lucide-react";
import AuthComponent from "./AuthComponent";
import RegisterComponent from "../Register/RegisterComponent";
import { useVendo } from "../../../hooks/useVendo";
import translator from "../../Extra/Translation/Translate";
import AOS from "aos";
import { useAppSelector } from "../../../globalState/hook";
import TwoFALogin from "./TwoFALogin";

export default function LoginNodeSoftware() {
    const [showLogin, setShowLogin] = useState(false);
    const { need2fa } = useAppSelector((store) => store.auth);
    const [showRegister, setShowRegister] = useState(false);
    const { connectionButton } = useVendo();
    const { translate } = translator();

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const mode = params.get("mode");

        if (mode === "login") {
            setShowLogin(true);
            setShowRegister(false);
        } else if (mode === "register") {
            setShowLogin(false);
            setShowRegister(true);
        } else {
            setShowLogin(false);
            setShowRegister(false);
        }
    }, []);

    const updateURL = (mode: string | null) => {
        const url = new URL(window.location.href);
        if (mode) {
            url.searchParams.set("mode", mode);
        } else {
            url.searchParams.delete("mode");
        }
        window.history.pushState({}, "", url);
    };

    const handleShowLogin = () => {
        setShowLogin(true);
        setShowRegister(false);
        updateURL("login");
    };

    const handleShowRegister = () => {
        setShowLogin(false);
        setShowRegister(true);
        updateURL("register");
    };

    const handleGoBack = () => {
        setShowLogin(false);
        setShowRegister(false);
        updateURL(null);
        setTimeout(() => {
            AOS.refresh();
        }, 10);
    };

    return (
        <div className="relative min-h-screen w-full bg-[#384146] text-white overflow-hidden">
            {/* Image de fond en full screen */}
            <div
                className="absolute inset-0 mt-14 bg-cover bg-center brightness-90"
                style={{
                    backgroundImage: "url('/images/node.jpg')",
                }}
            >
                <div className="absolute inset-0 bg-gradient-to-t from-[#384146] to-transparent opacity-90"></div>
            </div>

            {/* Contenu au-dessus de l'image */}
            <div className="relative z-10 mx-2 flex flex-col lg:flex-row items-center justify-center min-h-screen">
                {/* Texte à gauche */}
                <div className="hidden lg:flex flex-col items-center justify-end -translate-x-1/4 pl-16 text-center w-1/2 space-y-4">
                    <h2
                        data-aos="fade-left"
                        className="text-3xl font-bold tracking-wider bg-gradient-to-r from-slate-300 via-orange-50 to-orange-100 bg-clip-text text-transparent"
                    >
                        {translate("LoginNodeSoftware", "LEFT_TITLE")}
                    </h2>
                    <p
                        data-aos="fade-right"
                        className="text-xl font-light tracking-wide"
                    >
                        {translate("LoginNodeSoftware", "LEFT_SUBTITLE")}
                    </p>
                </div>

                {/* Formulaire à droite */}
                {showLogin ? (
                    <div className="w-full max-w-md space-y-5">
                        {/* Bouton retour */}
                        <button
                            onClick={handleGoBack}
                            className="flex items-center mb-3"
                            aria-label={translate(
                                "LoginNodeSoftware",
                                "BACK_BUTTON_ARIA",
                            )}
                        >
                            <CircleArrowLeft className="size-7 text-clisha-500" />
                        </button>
                        {need2fa ? <TwoFALogin /> : <AuthComponent />}
                    </div>
                ) : showRegister ? (
                    <div className="w-full max-w-md pt-16 space-y-5">
                        {/* Bouton retour */}
                        <button
                            onClick={handleGoBack}
                            className="flex items-center mb-3"
                            aria-label={translate(
                                "LoginNodeSoftware",
                                "BACK_BUTTON_ARIA",
                            )}
                        >
                            <CircleArrowLeft className="size-7 text-clisha-500" />
                        </button>
                        <RegisterComponent />
                    </div>
                ) : (
                    <div
                        data-aos="fade-left"
                        className="w-full max-w-md bg-[#2a3236] bg-opacity-90 p-8 rounded-2xl shadow-2xl space-y-8"
                    >
                        <div className="text-center">
                            <h1 className="text-3xl pb-2 font-bold tracking-tight bg-gradient-to-r from-orange-400 via-amber-400 to-orange-500 bg-clip-text text-transparent">
                                {translate("LoginNodeSoftware", "MAIN_TITLE")}
                            </h1>
                            <div className="h-px w-full bg-gradient-to-r from-transparent via-[#fc9828] to-transparent mb-4"></div>
                            <p className="mt-2 text-sm text-gray-300">
                                {translate(
                                    "LoginNodeSoftware",
                                    "MAIN_SUBTITLE",
                                )}
                            </p>
                        </div>

                        <div className="space-y-6">
                            <button
                                onClick={handleShowRegister}
                                className="group relative flex w-full justify-center rounded-lg border border-transparent bg-gradient-to-r from-orange-500 via-amber-500 to-orange-600 hover:from-orange-500 hover:via-amber-400 hover:to-orange-500 hover:shadow-2xl hover:shadow-orange-500/25 px-4 py-3 text-sm font-medium text-white hover:bg-[#e88a1e] focus:outline-none focus:ring-2 focus:ring-[#fc9828] focus:ring-offset-2 transition-all duration-200"
                            >
                                {translate(
                                    "LoginNodeSoftware",
                                    "REGISTER_BUTTON",
                                )}
                            </button>

                            <button
                                onClick={handleShowLogin}
                                className="group relative flex w-full justify-center rounded-lg border border-[#fc9828] bg-transparent px-4 py-3 text-sm font-medium text-[#fc9828] hover:bg-[#fc9828]/10 focus:outline-none focus:ring-2 focus:ring-[#fc9828] focus:ring-offset-2 transition-all duration-200"
                            >
                                {translate("LoginNodeSoftware", "LOGIN_BUTTON")}
                            </button>

                            {connectionButton({
                                className:
                                    "!rounded-lg !border !border-gray-500 !bg-transparent text-gray-300 hover:!bg-gray-700/30 !font-medium focus:!outline-none focus:!ring-2 focus:!ring-gray-500 focus:!ring-offset-2 transition-all duration-200",
                            })}
                        </div>

                        <div className="mt-6">
                            <div className="relative">
                                <div className="absolute inset-0 flex items-center">
                                    <div className="w-full border-t border-gray-600"></div>
                                </div>
                                <div className="relative flex justify-center text-sm">
                                    <span className="bg-[#2a3236] px-2 text-gray-400">
                                        {translate(
                                            "LoginNodeSoftware",
                                            "SECURE_BLOCKCHAIN",
                                        )}
                                    </span>
                                </div>
                            </div>
                        </div>

                        <div className="flex items-center justify-center space-x-4">
                            <div className="h-2 w-2 rounded-full bg-[#fc9828] animate-pulse"></div>
                            <div className="h-2 w-2 rounded-full bg-[#fc9828] animate-pulse delay-150"></div>
                            <div className="h-2 w-2 rounded-full bg-[#fc9828] animate-pulse delay-300"></div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
